import React, { useCallback } from "react";

import cn from "classnames";
import { defineMessage, useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import {
  IFilterComponentProps,
  IFilterInstance,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import { LegacySimpleCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { NumberField, NumberFieldComponent } from "@mapmycustomers/ui";

import i18nService from "@app/config/I18nService";

import styles from "./DurationRangeFilter.module.scss";
import { DurationFilterOptions, getDurationConverters } from "./durationUnit";

const doesSupportValue = (value: any, operator: FilterOperator) => {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "number" &&
    typeof value[1] === "number" &&
    operator === FilterOperator.IN_RANGE
  );
};

const descriptionMessage = defineMessage({
  id: "filters.numberRange.description",
  defaultMessage: "{fieldName} is between {from} and {to}",
  description: "NumberRangeFilter human-readable description",
});

interface DurationRangeFilterProps extends IFilterComponentProps {}

export const getDurationRangeFilter = (options: DurationFilterOptions = {}): IFilterInstance => {
  const { minutesToValue, valueToMinutes } = getDurationConverters(options.inSeconds ?? false);

  return {
    doesSupportValue,
    getComponent:
      (): ((props: DurationRangeFilterProps) => JSX.Element) =>
      ({ className, disabled, focus, onChange, placeholder, value }: DurationRangeFilterProps) => {
        const intl = useIntl();

        const rangeValue =
          Array.isArray(value.value) && value.value.length === 2
            ? value.value
            : [undefined, undefined];

        const setRef = useCallback(
          (ref: null | NumberFieldComponent) => {
            if (focus && ref) {
              ref.focus();
            }
          },
          [focus]
        );

        return (
          <div className={cn(styles.container, className)}>
            <NumberField
              disabled={disabled}
              onChange={(number) =>
                onChange?.({ ...value, value: [minutesToValue(number), rangeValue[1]] })
              }
              placeholder={placeholder as string}
              ref={setRef}
              size="middle"
              value={valueToMinutes(rangeValue[0]) ?? ""}
            />
            <span className={styles.conjunction}>
              {intl.formatMessage({
                id: "filters.numberRange.conjunction.and",
                defaultMessage: "and",
                description: "Conjunction between from and to fields",
              })}
            </span>
            <NumberField
              disabled={disabled}
              onChange={(number) =>
                onChange?.({ ...value, value: [rangeValue[0], minutesToValue(number)] })
              }
              size="middle"
              value={valueToMinutes(rangeValue[1]) ?? ""}
            />
            <span className={styles.conjunction}>
              {intl.formatMessage({
                id: "filters.duration.suffix",
                defaultMessage: "mins",
                description: "Mins suffix in duration field filter",
              })}
            </span>
          </div>
        );
      },
    getHumanReadableDescription: (value: LegacySimpleCondition, field: IField) => {
      if (!doesSupportValue(value.value, value.operator)) {
        return undefined;
      }
      const [from, to] = value.value;
      const fromMinutes = valueToMinutes(from);
      const toMinutes = valueToMinutes(to);
      return i18nService.formatMessage(
        descriptionMessage,
        `${field.displayName} is between ${fromMinutes} and ${toMinutes}`,
        {
          fieldName: field.displayName,
          from: fromMinutes,
          to: toMinutes,
        }
      );
    },
  };
};

export default getDurationRangeFilter();
